import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

// A quiet, brand-consistent 404 view. Mirrors the editorial tone of ComingSoon.
// Emits <meta name="robots" content="noindex,follow"> so Google does not index
// this page as a soft 404. Also sets a distinct <title> so accidental shares
// of a broken URL do not render the site's homepage title.
export default function NotFound() {
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const originalTitle = document.title;
    document.title = "Page not found — The Thriving Life 101";

    let robots = document.querySelector('meta[name="robots"]');
    const created = !robots;
    const previousContent = robots ? robots.getAttribute("content") : null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex,follow");

    return () => {
      document.title = originalTitle;
      if (created) {
        robots.parentNode && robots.parentNode.removeChild(robots);
      } else if (previousContent) {
        robots.setAttribute("content", previousContent);
      }
    };
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-24" data-testid="not-found-page">
      <div className="max-w-xl mx-auto px-6 text-center space-y-8">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">
          {t("notFound.eyebrow")}
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif text-primary-custom tracking-tight leading-tight">
          {t("notFound.heading")}
        </h1>
        <p className="font-serif text-base sm:text-lg italic text-secondary-custom leading-relaxed font-light">
          {t("notFound.body")}
        </p>
        <div className="pt-2">
          <Link to="/" data-testid="not-found-home-link" className="inline-flex items-center gap-2 border border-primary px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] transition-all duration-300">
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>{t("notFound.returnHome")}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
